/**
 * OnboardingWizard Component
 *
 * 3-screen wizard for creating and provisioning a new tenant.
 * Screen 1: Basics (name, slug, email, isolation, tier)
 * Screen 2: Configuration (template, features, password)
 * Screen 3: Review & Create -> Progress view
 *
 * WQ-127: Tenant Onboarding Wizard
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { OnboardingProgress } from './OnboardingProgress';

type AuthFetch = (url: string, options?: RequestInit) => Promise<Response>;

interface OnboardingWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
  authFetch: AuthFetch;
}

interface OnboardingStep {
  step: number;
  name: string;
  status: 'pending' | 'in_progress' | 'success' | 'failed' | 'skipped';
  message?: string;
}

const ISOLATION_TIER_MAP: Record<string, string> = {
  row: 'starter',
  schema: 'professional',
  container: 'enterprise',
};

const TIER_DESCRIPTIONS: Record<string, string> = {
  starter: 'Basic features, shared database with RLS',
  professional: 'Advanced analytics, dedicated schema',
  enterprise: 'Full isolation, custom models, priority support',
};

const TIER_FEATURES: Record<string, string[]> = {
  starter: ['aiChat', 'aiModels', 'usageAnalytics'],
  professional: ['aiChat', 'aiModels', 'usageAnalytics', 'advancedAnalytics', 'customBranding', 'dataExport'],
  enterprise: ['aiChat', 'aiModels', 'usageAnalytics', 'advancedAnalytics', 'customBranding', 'customModels', 'apiAccess', 'dataExport', 'ssoIntegration', 'auditExport', 'prioritySupport', 'corporateOs'],
};

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63);
}

export const OnboardingWizard: React.FC<OnboardingWizardProps> = ({
  isOpen,
  onClose,
  onComplete,
  authFetch,
}) => {
  // Wizard screen: 1=Basics, 2=Config, 3=Review, 4=Progress
  const [screen, setScreen] = useState(1);
  const [error, setError] = useState<string | null>(null);

  // Form state - Screen 1
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugManual, setSlugManual] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const [adminName, setAdminName] = useState('');
  const [isolationLevel, setIsolationLevel] = useState('row');
  const [tier, setTier] = useState('starter');

  // Form state - Screen 2
  const [features, setFeatures] = useState<string[]>([]);

  // Progress state - Screen 4
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [progressSteps, setProgressSteps] = useState<OnboardingStep[]>([]);
  const [isProgressComplete, setIsProgressComplete] = useState(false);
  const [isProgressFailed, setIsProgressFailed] = useState(false);
  const [adminTempPassword, setAdminTempPassword] = useState<string | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Auto-generate slug from name
  useEffect(() => {
    if (!slugManual && name) {
      setSlug(slugify(name));
    }
  }, [name, slugManual]);

  // Auto-select tier from isolation level
  useEffect(() => {
    setTier(ISOLATION_TIER_MAP[isolationLevel] || 'starter');
  }, [isolationLevel]);

  // Auto-fill features from tier
  useEffect(() => {
    setFeatures([...(TIER_FEATURES[tier] || [])]);
  }, [tier]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const pollStatus = useCallback(async (id: string) => {
    try {
      const resp = await authFetch(`/api/admin/tenants/${id}/onboarding/status`);
      if (!resp.ok) return;
      const data = await resp.json();

      if (data.steps) {
        setProgressSteps(data.steps);
      }

      if (data.status === 'completed') {
        setIsProgressComplete(true);
        setAdminTempPassword(data.admin_temp_password);
        if (pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      } else if (data.status === 'failed') {
        setIsProgressFailed(true);
        if (pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      }
    } catch {
      // Polling error — ignore, will retry
    }
  }, [authFetch]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      const resp = await authFetch('/api/admin/tenants', {
        method: 'POST',
        body: JSON.stringify({
          slug,
          name,
          admin_email: adminEmail,
          admin_name: adminName || undefined,
          isolation_level: isolationLevel,
          tier,
          features,
        }),
      });

      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        setError(data.detail || `Create failed (${resp.status})`);
        setIsSubmitting(false);
        return;
      }

      const tenant = await resp.json();
      setTenantId(tenant.id);
      setScreen(4);

      // Step 1 is always "success" (record created)
      setProgressSteps([{ step: 1, name: 'Create tenant record', status: 'success', message: 'Done' }]);

      // Start polling for onboarding progress
      pollRef.current = setInterval(() => pollStatus(tenant.id), 2000);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Network error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRetry = async () => {
    if (!tenantId) return;
    setIsProgressFailed(false);
    setProgressSteps([]);
    setError(null);

    try {
      await authFetch(`/api/admin/tenants/${tenantId}/provision`, { method: 'POST' });
      pollRef.current = setInterval(() => pollStatus(tenantId), 2000);
    } catch {
      setError('Retry failed');
    }
  };

  const handleClose = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (isProgressComplete) {
      onComplete();
    }
    onClose();
    // Reset state
    setScreen(1);
    setName('');
    setSlug('');
    setSlugManual(false);
    setAdminEmail('');
    setAdminName('');
    setIsolationLevel('row');
    setTier('starter');
    setFeatures([]);
    setTenantId(null);
    setProgressSteps([]);
    setIsProgressComplete(false);
    setIsProgressFailed(false);
    setAdminTempPassword(undefined);
    setError(null);
  };

  // Validation
  const isScreen1Valid = name.length >= 2 && slug.length >= 3 && adminEmail.includes('@');

  const toggleFeature = (f: string) => {
    setFeatures(prev => prev.includes(f) ? prev.filter(x => x !== f) : [...prev, f]);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div
        className="modal modal-lg"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '640px', maxHeight: '85vh', overflow: 'auto' }}
      >
        <div className="modal-header">
          <h2>
            {screen <= 3 ? 'Create Tenant' : 'Onboarding Progress'}
            {screen <= 3 && (
              <span style={{ fontSize: '13px', fontWeight: 400, color: '#9ca3af', marginLeft: '8px' }}>
                Step {screen} of 3
              </span>
            )}
          </h2>
          <button className="btn btn-ghost" onClick={handleClose}>&times;</button>
        </div>

        <div className="modal-body">
          {error && (
            <div className="alert alert-error" style={{ marginBottom: '16px', padding: '10px 16px', borderRadius: '8px', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', color: '#fca5a5', fontSize: '13px' }}>
              {error}
              <button onClick={() => setError(null)} style={{ marginLeft: '8px', cursor: 'pointer', background: 'none', border: 'none', color: '#fca5a5' }}>&times;</button>
            </div>
          )}

          {/* Screen 1: Basics */}
          {screen === 1 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label className="form-label">Tenant Name *</label>
                <input
                  className="form-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Acme Corporation"
                  autoFocus
                />
              </div>
              <div>
                <label className="form-label">URL Slug *</label>
                <input
                  className="form-input"
                  value={slug}
                  onChange={(e) => { setSlug(e.target.value); setSlugManual(true); }}
                  placeholder="acme-corp"
                />
                <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                  Lowercase letters, numbers, hyphens only
                </div>
              </div>
              <div>
                <label className="form-label">Admin Email *</label>
                <input
                  className="form-input"
                  type="email"
                  value={adminEmail}
                  onChange={(e) => setAdminEmail(e.target.value)}
                  placeholder="admin@acme.com"
                />
              </div>
              <div>
                <label className="form-label">Admin Name</label>
                <input
                  className="form-input"
                  value={adminName}
                  onChange={(e) => setAdminName(e.target.value)}
                  placeholder="John Doe"
                />
              </div>
              <div>
                <label className="form-label">Isolation Level</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {['row', 'schema', 'container'].map((level) => (
                    <label key={level} style={{
                      flex: 1,
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: isolationLevel === level ? '2px solid #7c3aed' : '1px solid #374151',
                      background: isolationLevel === level ? 'rgba(124,58,237,0.1)' : 'transparent',
                      cursor: 'pointer',
                      fontSize: '13px',
                      textAlign: 'center',
                    }}>
                      <input
                        type="radio"
                        name="isolation"
                        value={level}
                        checked={isolationLevel === level}
                        onChange={() => setIsolationLevel(level)}
                        style={{ display: 'none' }}
                      />
                      <div style={{ fontWeight: 600, textTransform: 'capitalize' }}>{level}</div>
                      <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
                        {level === 'row' && 'Shared DB + RLS'}
                        {level === 'schema' && 'Dedicated schema'}
                        {level === 'container' && 'Full Docker stack'}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="form-label">Subscription Tier</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {['starter', 'professional', 'enterprise'].map((t) => (
                    <label key={t} style={{
                      flex: 1,
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: tier === t ? '2px solid #06b6d4' : '1px solid #374151',
                      background: tier === t ? 'rgba(6,182,212,0.1)' : 'transparent',
                      cursor: 'pointer',
                      fontSize: '13px',
                      textAlign: 'center',
                    }}>
                      <input
                        type="radio"
                        name="tier"
                        value={t}
                        checked={tier === t}
                        onChange={() => setTier(t)}
                        style={{ display: 'none' }}
                      />
                      <div style={{ fontWeight: 600, textTransform: 'capitalize' }}>{t}</div>
                      <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
                        {TIER_DESCRIPTIONS[t]}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Screen 2: Configuration */}
          {screen === 2 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label className="form-label">Feature Flags</label>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '6px',
                  padding: '12px',
                  background: 'rgba(0,0,0,0.2)',
                  borderRadius: '8px',
                }}>
                  {Object.entries({
                    aiChat: 'AI Chat',
                    aiModels: 'AI Models',
                    usageAnalytics: 'Usage Analytics',
                    advancedAnalytics: 'Advanced Analytics',
                    customBranding: 'Custom Branding',
                    customModels: 'Custom Models',
                    apiAccess: 'API Access',
                    dataExport: 'Data Export',
                    ssoIntegration: 'SSO Integration',
                    auditExport: 'Audit Export',
                    prioritySupport: 'Priority Support',
                    corporateOs: 'Corporate OS',
                  }).map(([key, label]) => (
                    <label key={key} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '13px',
                      cursor: 'pointer',
                    }}>
                      <input
                        type="checkbox"
                        checked={features.includes(key)}
                        onChange={() => toggleFeature(key)}
                      />
                      {label}
                    </label>
                  ))}
                </div>
                <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                  Pre-filled from {tier} tier defaults. You can customize.
                </div>
              </div>
            </div>
          )}

          {/* Screen 3: Review */}
          {screen === 3 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <h3 style={{ fontSize: '15px', fontWeight: 600, marginBottom: '4px' }}>Review Tenant Configuration</h3>
              <div style={{
                padding: '16px',
                background: 'rgba(0,0,0,0.2)',
                borderRadius: '8px',
                display: 'grid',
                gridTemplateColumns: '120px 1fr',
                gap: '8px',
                fontSize: '13px',
              }}>
                <div style={{ color: '#9ca3af' }}>Name</div>
                <div>{name}</div>
                <div style={{ color: '#9ca3af' }}>Slug</div>
                <div><code>{slug}</code></div>
                <div style={{ color: '#9ca3af' }}>Admin Email</div>
                <div>{adminEmail}</div>
                {adminName && <>
                  <div style={{ color: '#9ca3af' }}>Admin Name</div>
                  <div>{adminName}</div>
                </>}
                <div style={{ color: '#9ca3af' }}>Isolation</div>
                <div style={{ textTransform: 'capitalize' }}>{isolationLevel}</div>
                <div style={{ color: '#9ca3af' }}>Tier</div>
                <div style={{ textTransform: 'capitalize' }}>{tier}</div>
                <div style={{ color: '#9ca3af' }}>Features</div>
                <div>{features.length} enabled</div>
              </div>

              <div style={{
                padding: '10px 16px',
                background: 'rgba(124,58,237,0.1)',
                border: '1px solid rgba(124,58,237,0.3)',
                borderRadius: '8px',
                fontSize: '12px',
                color: '#c4b5fd',
              }}>
                Clicking "Create & Provision" will create the tenant and run the 10-step onboarding pipeline automatically.
                A temporary admin password will be generated and shown once.
              </div>
            </div>
          )}

          {/* Screen 4: Progress */}
          {screen === 4 && (
            <OnboardingProgress
              tenantName={name}
              steps={progressSteps}
              isComplete={isProgressComplete}
              isFailed={isProgressFailed}
              adminTempPassword={adminTempPassword}
              onRetry={handleRetry}
              onClose={handleClose}
            />
          )}
        </div>

        {/* Footer navigation (screens 1-3 only) */}
        {screen <= 3 && (
          <div className="modal-footer" style={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: '12px 20px',
            borderTop: '1px solid #374151',
          }}>
            <div>
              {screen > 1 && (
                <button className="btn btn-ghost" onClick={() => setScreen(screen - 1)}>
                  Back
                </button>
              )}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button className="btn btn-ghost" onClick={handleClose}>
                Cancel
              </button>
              {screen < 3 ? (
                <button
                  className="btn btn-primary"
                  onClick={() => setScreen(screen + 1)}
                  disabled={screen === 1 && !isScreen1Valid}
                >
                  Next
                </button>
              ) : (
                <button
                  className="btn btn-primary"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Creating...' : 'Create & Provision'}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
